import { useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import './suboutRegistration.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { UsDateInput } from '../../../components/UsDateInput';
import { useAuthStore } from '../../../auth/store';
import { fmtLocalDate } from '../../../utils/datetime';
import { apiMessage, fetchSampleDetails, registerSubout, updateTestOrder } from './api';
import type {
  SuboutLabItem, SuboutLookups, SuboutRegistrationRow, SuboutSampleDetailRow,
  SuboutTestOrderDetail,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Subout Registration — full-page detail view (New), matching legacy ALPACA
// SubOutSampleRegistrations_DetailView_Copy. The list is REPLACED by this page
// (standing rule 12). Sections: Subout Info · Contract Lab Info · Shipping Info ·
// Test Info, then the Subout Samples / Attachments / Notes tabs. Save /
// Save & Close create the order (AI_SuboutRegistration_Register_SP) and return to
// the list; Cancel / Delete discard.
//
// DEFERRED (shown but not wired — separate features): Reports (Subout COC),
// Project Notes, and the Attachments / Notes tab CONTENT.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  samples?: SuboutRegistrationRow[];  // NEW mode: the selected PENDING rows being registered
  edit?: { order: SuboutTestOrderDetail; samples: SuboutSampleDetailRow[] };  // EDIT mode (Subout Test Order)
  lookups: SuboutLookups | null;
  canCreate: boolean;                 // create (new) or write (edit) right
  onClose: () => void;                // Cancel / Delete → back to list
  onSaved: () => void;                // after a successful save → refresh + list
}

const EMPTY_LAB: SuboutLabItem = {
  id: '', name: null, accreditationId: null, contact: null, phone: null, email: null, address: null,
};

const SAMPLE_COLUMNS: AppColumn<SuboutSampleDetailRow>[] = [
  { field: 'jobId',           header: 'JobID',          width: 110, text: (r) => r.jobId ?? '' },
  { field: 'sampleId',        header: 'SampleID',       width: 130, text: (r) => r.sampleId ?? '' },
  { field: 'matrix',          header: 'Matrix',         width: 110, text: (r) => r.matrix ?? '' },
  { field: 'test',            header: 'Test',           width: 150, text: (r) => r.test ?? '' },
  { field: 'method',          header: 'Method',         width: 120, text: (r) => r.method ?? '' },
  { field: 'parameter',       header: 'Parameter',      width: 180, text: (r) => r.parameter ?? '' },
  { field: 'containerType',   header: 'ContainerType',  width: 140, text: (r) => r.containerType ?? '' },
  { field: 'holdTime',        header: 'HoldTime',       width: 100, text: (r) => r.holdTime ?? '' },
  { field: 'dateCollected',   header: 'DateCollected',  width: 120, type: 'date', text: (r) => fmtLocalDate(r.dateCollected) },
  { field: 'originalDueDate', header: 'OriginalDueDate', width: 130, type: 'date', text: (r) => fmtLocalDate(r.originalDueDate) },
  { field: 'client',          header: 'Client',         width: 200, text: (r) => r.client ?? '' },
];

type Tab = 'samples' | 'attachments' | 'notes';

export default function SuboutRegistrationDetail({ samples = [], edit, lookups, canCreate, onClose, onSaved }: Props) {
  const notify = useNotify();
  const confirm = useConfirm();
  const userName = useAuthStore((s) => s.user?.name) ?? 'Service';
  const o = edit?.order;
  const isEdit = !!edit;

  // ── Editable fields (pre-filled from the order in Edit mode) ────────────────
  const [labOid, setLabOid]   = useState(o?.contractLabOid ?? '');
  const [tatOid, setTatOid]   = useState(o?.turnAroundTimeOid ?? '');
  const [bottles, setBottles] = useState(o ? String(o.numberOfBottles ?? '') : String(new Set(samples.map((s) => s.sampleId)).size || 1));
  const [preservativeOid, setPreservativeOid] = useState(o?.preservativeOid ?? '');
  const [deliveryOid, setDeliveryOid] = useState(o?.deliveryServiceOid ?? '');
  const [trackingNo, setTrackingNo]   = useState(o?.trackingNo ?? '');
  const [remark, setRemark]   = useState(o?.remark ?? '');
  const [dateShipped, setDateShipped] = useState(o?.dateShipped ? o.dateShipped.slice(0, 10) : '');
  const [dueDate, setDueDate]         = useState(o?.dueDate ? o.dueDate.slice(0, 10) : '');
  const [shipContact, setShipContact] = useState(o?.deliveryContact ?? '');
  const [shipPhone, setShipPhone]     = useState(o?.deliveryContactPhone ?? '');
  const [shipEmail, setShipEmail]     = useState(o?.deliveryEmail ?? '');

  // Contract Lab Info — editable + REQUIRED (ALPACA parity), auto-filled from the
  // lab when one is picked, then overridable.
  const [labContact, setLabContact] = useState(o?.contact ?? '');
  const [labPhone, setLabPhone]     = useState(o?.contactPhone ?? '');
  const [labAddr, setLabAddr]       = useState(o?.labAddress ?? '');

  const [tab, setTab]   = useState<Tab>('samples');
  const [busy, setBusy] = useState(false);

  // Accreditation ID / Contact Email are read-only, derived from the lab.
  const lab = useMemo<SuboutLabItem>(
    () => lookups?.contractLabs.find((l) => l.id === labOid) ?? EMPTY_LAB,
    [lookups, labOid]);

  // Pick a lab → fill the editable Contact Phone / Contact / Lab Address.
  function pickLab(id: string) {
    setLabOid(id);
    const l = lookups?.contractLabs.find((x) => x.id === id);
    setLabContact(l?.contact ?? '');
    setLabPhone(l?.phone ?? '');
    setLabAddr(l?.address ?? '');
  }

  // ── Test Info ──────────────────────────────────────────────────────────────
  const srcSamples = useMemo<{ test: string | null; sampleId: string | null }[]>(
    () => (isEdit ? edit!.samples : samples), [isEdit, edit, samples]);
  const testForSubout = useMemo(
    () => [...new Set(srcSamples.map((s) => s.test).filter(Boolean))].join(', '), [srcSamples]);
  const numberOfSamples = useMemo(() => new Set(srcSamples.map((s) => s.sampleId)).size, [srcSamples]);

  // ── Subout Samples grid ────────────────────────────────────────────────────
  const [sampleRows, setSampleRows] = useState<SuboutSampleDetailRow[]>(edit?.samples ?? []);
  const [gridLoading, setGridLoading] = useState(false);

  const loadSamples = useMemo(() => async () => {
    if (isEdit) return;   // Edit mode uses the order's samples passed in
    setGridLoading(true);
    try {
      setSampleRows(await fetchSampleDetails(
        samples.map((s) => ({ sampleOid: s.sampleOid, testMethodOid: s.testMethodOid }))));
    } catch (e) {
      notify.error(apiMessage(e, 'Failed to load the subout samples.'));
    } finally {
      setGridLoading(false);
    }
  }, [isEdit, samples, notify]);

  useEffect(() => { void loadSamples(); }, [loadSamples]);

  // ── Save (register in New mode, update in Edit mode) ────────────────────────
  async function save(close: boolean) {
    if (busy) return;
    if (!canCreate) { notify.warning(`You do not have permission to ${isEdit ? 'edit this subout order' : 'register subout samples'}.`); return; }
    // Required fields (ALPACA parity): Contract Lab, Contact Phone, Contact,
    // Lab Address, Turn Around Time — plus Tracking# in Edit (Test Order) mode.
    const missing: string[] = [];
    if (!labOid)            missing.push('Contract Lab');
    if (!labPhone.trim())   missing.push('Contact Phone');
    if (!labContact.trim()) missing.push('Contact');
    if (!labAddr.trim())    missing.push('Lab Address');
    if (!tatOid)            missing.push('Turn Around Time');
    if (isEdit && !trackingNo.trim()) missing.push('Tracking#');
    if (missing.length) { notify.warning(`Please fill the required field(s): ${missing.join(', ')}.`); return; }

    const ok = await confirm.confirm({
      title: 'Do you want to Save?',
      message: isEdit
        ? `Save changes to subout order ${o?.suboutOrderId}?`
        : `Register ${numberOfSamples} sample${numberOfSamples === 1 ? '' : 's'} (${samples.length} test${samples.length === 1 ? '' : 's'}) to a new subout order?`,
    });
    if (!ok) return;

    setBusy(true);
    try {
      if (isEdit) {
        const updated = await updateTestOrder(o!.id!, {
          contractLabOid: labOid,
          turnAroundTimeOid: tatOid || null,
          numberOfBottles: bottles ? Number(bottles) : null,
          preservativeOid: preservativeOid || null,
          deliveryServiceOid: deliveryOid || null,
          trackingNo: trackingNo.trim() || null,
          remark: remark.trim() || null,
          dateShipped: dateShipped || null,
          labAddress: labAddr.trim() || null,
          contact: labContact.trim() || null,
          contactPhone: labPhone.trim() || null,
          deliveryContact: shipContact.trim() || null,
          deliveryContactPhone: shipPhone.trim() || null,
          deliveryEmail: shipEmail.trim() || null,
        });
        notify.success(`Subout order ${updated.suboutOrderId} saved successfully.`);
        onSaved();
      } else {
        const res = await registerSubout({
          items: samples.map((s) => ({ sampleOid: s.sampleOid, testMethodOid: s.testMethodOid })),
          contractLabOid: labOid,
          turnAroundTimeOid: tatOid || null,
          numberOfBottles: bottles ? Number(bottles) : null,
          preservativeOid: preservativeOid || null,
          deliveryServiceOid: deliveryOid || null,
          trackingNo: trackingNo.trim() || null,
          remark: remark.trim() || null,
          dateShipped: dateShipped || null,
          dueDate: dueDate || null,
          labAddress: labAddr.trim() || null,
          contact: labContact.trim() || null,
          contactPhone: labPhone.trim() || null,
          deliveryContact: shipContact.trim() || null,
          deliveryContactPhone: shipPhone.trim() || null,
          deliveryEmail: shipEmail.trim() || null,
        });
        notify.success(`Subout order ${res.orderId} created — ${res.registered} sample test${res.registered === 1 ? '' : 's'} registered.`);
        onSaved();
      }
      void close;   // new-only distinction not needed; both finish to the list
    } catch (e) {
      notify.error(apiMessage(e, isEdit ? 'Failed to save the order.' : 'The registration failed.'));
    } finally {
      setBusy(false);
    }
  }

  const opt = (items?: { id: string; name: string | null }[]) =>
    (items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name ?? '—'}</option>);

  const actionBar = (
    <div className="srd-actions">
      <div className="srd-actions-left">
        <button type="button" className="ca-btn" disabled title="Reports (Subout COC) — not yet available">
          <i className="ti ti-file-text" aria-hidden="true" /> Reports
        </button>
        <button type="button" className="ca-btn" onClick={onClose} disabled={busy}>
          <i className="ti ti-trash" aria-hidden="true" /> Delete
        </button>
        <button type="button" className="ca-btn" onClick={() => void loadSamples()} disabled={busy || gridLoading}>
          <i className="ti ti-refresh" aria-hidden="true" /> Refresh
        </button>
        <button type="button" className="ca-btn" disabled title="Project Notes — not yet available">
          <i className="ti ti-notes" aria-hidden="true" /> Project Notes (0)
        </button>
      </div>
      <div className="srd-actions-right">
        <button type="button" className="ca-btn ca-btn-solid" onClick={() => void save(false)} disabled={busy}>
          <i className="ti ti-device-floppy" aria-hidden="true" /> Save
        </button>
        <button type="button" className="ca-btn ca-btn-solid" onClick={() => void save(true)} disabled={busy}>
          <i className="ti ti-check" aria-hidden="true" /> Save and Close
        </button>
        <button type="button" className="ca-btn" onClick={onClose} disabled={busy}>
          <i className="ti ti-x" aria-hidden="true" /> Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="bl-page srd-page" data-unsaved-ignore>
      {/* Breadcrumb + title */}
      <div className="srd-head">
        <div className="dp-breadcrumb">
          <span>Sample SubContract Tracking</span><i className="ti ti-chevron-right" />
          <span>{isEdit ? 'Subout Test Order' : 'Subout Registration'}</span><i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{isEdit ? (o?.suboutOrderId ?? 'Edit') : 'New'}</span>
        </div>
        <h2 className="srd-title">{isEdit ? 'Subout Test Order' : 'Subout Registration'}</h2>
      </div>

      {actionBar}

      <div className="srd-body">
        {/* ── Subout Info ── */}
        <section className="srd-sec">
          <h3>Subout Info</h3>
          <div className="srd-grid">
            <label>Subout Order ID</label>
            <input value={isEdit ? (o?.suboutOrderId ?? '') : 'AutoGenerate'} readOnly className="srd-ro" />
            <label>Registered By</label>
            <input value={isEdit ? (o?.registeredBy ?? '') : userName} readOnly className="srd-ro" />
            <label>Date Registered</label>
            <input value={fmtLocalDate(isEdit ? o?.dateRegistered : new Date().toISOString())} readOnly className="srd-ro" />
            {isEdit ? <><label>Status</label><input value={o?.statusText ?? ''} readOnly className="srd-ro" /></> : <><span /><span /></>}
          </div>
        </section>

        {/* ── Contract Lab Info ── */}
        <section className="srd-sec">
          <h3>Contract Lab Info</h3>
          <div className="srd-grid">
            <label>Contract Lab<span className="srd-req">*</span></label>
            <select className={labOid ? '' : 'srd-empty'} value={labOid} onChange={(e) => pickLab(e.target.value)}>
              <option value="">— select —</option>
              {opt(lookups?.contractLabs)}
            </select>
            <label>Contact Phone<span className="srd-req">*</span></label>
            <input className={labPhone.trim() ? '' : 'srd-empty'} value={labPhone} maxLength={100}
                   onChange={(e) => setLabPhone(e.target.value)} />

            <label>Accreditation ID</label>
            <input value={lab.accreditationId ?? ''} readOnly className="srd-ro" />
            <label>Contact Email</label>
            <input value={lab.email ?? ''} readOnly className="srd-ro" />

            <label>Contact<span className="srd-req">*</span></label>
            <input className={labContact.trim() ? '' : 'srd-empty'} value={labContact} maxLength={100}
                   onChange={(e) => setLabContact(e.target.value)} />
            <label>Lab Address<span className="srd-req">*</span></label>
            <input className={labAddr.trim() ? '' : 'srd-empty'} value={labAddr} maxLength={100}
                   onChange={(e) => setLabAddr(e.target.value)} />
          </div>
        </section>

        {/* ── Shipping Info ── */}
        <section className="srd-sec">
          <h3>Shipping Info</h3>
          <div className="srd-grid">
            <label>Delivery Service</label>
            <select value={deliveryOid} onChange={(e) => setDeliveryOid(e.target.value)}>
              <option value="">— none —</option>
              {opt(lookups?.deliveryServices)}
            </select>
            <label>Tracking #{isEdit && <span className="srd-req">*</span>}</label>
            <input className={isEdit && !trackingNo.trim() ? 'srd-empty' : ''} value={trackingNo} maxLength={100}
                   onChange={(e) => setTrackingNo(e.target.value)} />

            <label>Date Shipped</label>
            <UsDateInput className="srd-date" value={dateShipped} onChange={setDateShipped} />
            <label>Contact Phone</label>
            <input value={shipPhone} maxLength={100} onChange={(e) => setShipPhone(e.target.value)} />

            <label>Contact</label>
            <input value={shipContact} maxLength={100} onChange={(e) => setShipContact(e.target.value)} />
            <label>Email</label>
            <input value={shipEmail} maxLength={100} onChange={(e) => setShipEmail(e.target.value)} />
          </div>
        </section>

        {/* ── Test Info ── */}
        <section className="srd-sec">
          <h3>Test Info</h3>
          <div className="srd-grid">
            <label>Test For Subout</label>
            <textarea value={testForSubout} readOnly className="srd-ro" rows={2} />
            <label>Turn Around Time<span className="srd-req">*</span></label>
            <select className={tatOid ? '' : 'srd-empty'} value={tatOid} onChange={(e) => setTatOid(e.target.value)}>
              <option value="">— select —</option>
              {opt(lookups?.turnAroundTimes)}
            </select>

            <label>Number Of Samples</label>
            <input value={String(numberOfSamples)} readOnly className="srd-ro" />
            <label>Due Date</label>
            {isEdit
              ? <input value={fmtLocalDate(o?.dueDate)} readOnly className="srd-ro" />
              : <UsDateInput className="srd-date" value={dueDate} onChange={setDueDate} />}

            <label>Number Of Bottles</label>
            <input type="number" min={0} value={bottles} onChange={(e) => setBottles(e.target.value)} />
            <label>Remark</label>
            <textarea value={remark} maxLength={4000} rows={2} onChange={(e) => setRemark(e.target.value)} />

            <label>Preservative</label>
            <select value={preservativeOid} onChange={(e) => setPreservativeOid(e.target.value)}>
              <option value="">— none —</option>
              {opt(lookups?.preservatives)}
            </select>
            <label>Result Import EDD</label>
            <input value="N/A" readOnly className="srd-ro" />
          </div>
        </section>

        {/* ── Bottom tabs ── */}
        <div className="srd-tabs" role="tablist">
          {(['samples', 'attachments', 'notes'] as Tab[]).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t}
                    className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
              {t === 'samples' ? `Subout Samples (${numberOfSamples})` : t === 'attachments' ? 'Attachments' : 'Notes'}
            </button>
          ))}
        </div>

        {tab === 'samples' ? (
          <div className="srd-tabpane">
            <AppDataGrid<SuboutSampleDetailRow>
              rows={sampleRows}
              columns={SAMPLE_COLUMNS}
              getRowId={(r) => `${r.sampleOid}:${r.testMethodOid}`}
              loading={gridLoading}
              maximizable
              exportFilename="subout-samples"
              emptyText="No samples."
            />
          </div>
        ) : (
          <div className="srd-tabpane srd-tab-stub">
            {tab === 'attachments'
              ? 'Attachments are not available on a new subout order yet.'
              : 'Notes are not available on a new subout order yet.'}
          </div>
        )}
      </div>

      {actionBar}
    </div>
  );
}
